import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { VRMLoaderPlugin, VRMUtils, type VRM } from '@pixiv/three-vrm';
import {
  VRMAnimationLoaderPlugin,
  createVRMAnimationClip,
  type VRMAnimation,
} from '@pixiv/three-vrm-animation';

/**
 * サムネイル自動生成用のフォールバック VRM。呼び出し側で vrmUrl を省略した場合のみ使われる
 * （通常は VrmContext が管理する選択中の VRM の URL が渡される）。
 * base 配下に配信されるため import.meta.env.BASE_URL を前置する。
 */
const SAMPLE_VRM_URL = `${import.meta.env.BASE_URL}samples/AvatarSample.vrm`;

const THUMB_SIZE = 512;

function createLoader(): GLTFLoader {
  const loader = new GLTFLoader();
  loader.register((parser) => new VRMLoaderPlugin(parser));
  loader.register((parser) => new VRMAnimationLoaderPlugin(parser));
  return loader;
}

/**
 * .vrma ファイルを VRM に適用し、指定した再生位置（duration に対する比率）の
 * フレームをオフスクリーンでレンダリングして JPEG の Blob を返す。
 * アップロード画面のプレビュー生成用。失敗時は例外を投げるので、呼び出し側でフォールバックすること。
 * vrmUrl は通常 VrmContext が管理する選択中の VRM の URL（省略時はサンプル VRM にフォールバック）。
 */
export async function generateVrmaThumbnail(
  vrmaFile: File,
  atRatio = 0.3,
  vrmUrl: string = SAMPLE_VRM_URL,
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = THUMB_SIZE;
  canvas.height = THUMB_SIZE;

  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: false,
    preserveDrawingBuffer: true,
  });
  renderer.setPixelRatio(1);
  renderer.setSize(THUMB_SIZE, THUMB_SIZE, false);
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  let vrm: VRM | undefined;

  try {
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x11181a);

    const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
    camera.position.set(0, 1.3, 2.4);
    camera.lookAt(0, 1.0, 0);

    const hemi = new THREE.HemisphereLight(0xffffff, 0x444455, 1.2);
    scene.add(hemi);
    const dir = new THREE.DirectionalLight(0xffffff, 1.4);
    dir.position.set(1, 2, 1.5);
    scene.add(dir);

    // ---- VRM を読み込み ----
    const vrmLoader = createLoader();
    const vrmGltf = await vrmLoader.loadAsync(vrmUrl);
    vrm = vrmGltf.userData.vrm as VRM;

    VRMUtils.removeUnnecessaryVertices(vrmGltf.scene);
    VRMUtils.combineSkeletons(vrmGltf.scene);
    VRMUtils.rotateVRM0(vrm);
    scene.add(vrm.scene);

    // ---- VRMA を読み込んでクリップ生成 ----
    const vrmaUrl = URL.createObjectURL(vrmaFile);
    let vrmaGltf;
    try {
      const vrmaLoader = createLoader();
      vrmaGltf = await vrmaLoader.loadAsync(vrmaUrl);
    } finally {
      URL.revokeObjectURL(vrmaUrl);
    }
    const vrmAnimations = vrmaGltf.userData.vrmAnimations as VRMAnimation[] | undefined;
    if (!vrmAnimations || vrmAnimations.length === 0) {
      throw new Error('VRMAnimation が含まれていません');
    }
    const clip = createVRMAnimationClip(vrmAnimations[0], vrm);

    // ---- 指定比率の位置までシークして1フレームだけ描画 ----
    const mixer = new THREE.AnimationMixer(vrm.scene);
    const action = mixer.clipAction(clip);
    action.play();
    const seekTime = clip.duration * Math.min(Math.max(atRatio, 0), 1);
    mixer.update(seekTime);
    vrm.update(0);

    renderer.render(scene, camera);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.85);
    });
    if (!blob) throw new Error('サムネイル画像の生成に失敗しました');
    return blob;
  } finally {
    if (vrm) VRMUtils.deepDispose(vrm.scene);
    renderer.dispose();
    renderer.forceContextLoss();
  }
}
